import { api, ensureApiAuthenticated } from '../services/api';

export interface ChannelApprovalSummary {
  outbox_event_id: string;
  aggregate_type: string | null;
  aggregate_id: string | null;
  event_type: string | null;
  source: string | null;
  occurred_at: string | null;
  queued_at: string | null;
  channel_targets: string[];
  target_channels: string[];
  variant_count: number;
  queue_ids: number[];
  last_error: string | null;
  batch_name: string | null;
  batch_submitted_at: string | null;
}

export interface ChannelApprovalLine {
  variant_id: string;
  product_id?: string;
  card_name?: string;
  set_name?: string | null;
  condition?: string;
  condition_label?: string;
  is_foil?: boolean;
  language?: string;
  printing?: string;
  quantity?: number;
  calculated_preferred_price?: number | null;
  preferred_price_override?: number | null;
  proposed_policy_price?: number | null;
  current_policy_price?: number | null;
  effective_base_price?: number | null;
  current_effective_base_price?: number | null;
  shopify_listing_price?: number | null;
  manapool_listing_price?: number | null;
  shopify_multiplier?: number;
  manapool_multiplier?: number;
  missing?: boolean;
}

export interface ChannelApprovalDetail {
  outbox_event_id: string;
  aggregate_type: string | null;
  aggregate_id: string | null;
  event_type: string | null;
  source: string | null;
  occurred_at: string | null;
  channel_targets: string[];
  batch: {
    id: string;
    name: string;
    status: string;
    item_count: number;
    submitted_at: string | null;
  } | null;
  queues: Array<{
    queue_id: number;
    status: string;
    target_channel: string;
    attempt_status: string;
    last_error: string | null;
  }>;
  lines: ChannelApprovalLine[];
  lines_missing_price: number;
  variants_updated?: number;
}

export interface ChannelApprovalFailure {
  queue_id: number;
  status: string;
  retry_count: number;
  last_error: string | null;
  created_at: string | null;
  next_retry_at: string | null;
  target_channel: string | null;
  attempt_status: string | null;
  outbox_event_id: string | null;
  event_type: string | null;
  source: string | null;
  aggregate_type: string | null;
  aggregate_id: string | null;
  variant_count: number;
  is_permanent_price_failure: boolean;
  is_credential_failure?: boolean;
}

export const listChannelApprovals = async (): Promise<{
  items: ChannelApprovalSummary[];
  total: number;
  failures: ChannelApprovalFailure[];
  failure_total: number;
}> => {
  await ensureApiAuthenticated();
  const response = await api.get<{
    items: ChannelApprovalSummary[];
    total: number;
    failures: ChannelApprovalFailure[];
    failure_total: number;
  }>('/admin/channel-approvals');
  return response.data;
};

export const abandonChannelDispatchFailures = async (payload: {
  queue_ids?: number[];
  abandon_all_price_failures?: boolean;
}): Promise<{ abandoned_count: number; abandoned_queue_ids: number[]; abandoned_by: string }> => {
  await ensureApiAuthenticated();
  const response = await api.post('/admin/channel-approvals/failures/abandon', payload);
  return response.data;
};

export const retryChannelDispatchFailures = async (payload: {
  queue_ids?: number[];
  retry_credential_failures_only?: boolean;
}): Promise<{ retried_count: number; retried_queue_ids: number[]; retried_by: string }> => {
  await ensureApiAuthenticated();
  const response = await api.post('/admin/channel-approvals/failures/retry', payload);
  return response.data;
};

export const getChannelApprovalDetail = async (outboxEventId: string): Promise<ChannelApprovalDetail> => {
  await ensureApiAuthenticated();
  const response = await api.get<ChannelApprovalDetail>(`/admin/channel-approvals/${outboxEventId}`);
  return response.data;
};

export const updateChannelApprovalPrices = async (
  outboxEventId: string,
  updates: Array<{ variant_id: string; preferred_price_override: number | null }>,
): Promise<ChannelApprovalDetail> => {
  await ensureApiAuthenticated();
  const response = await api.patch<ChannelApprovalDetail>(
    `/admin/channel-approvals/${outboxEventId}/variant-prices`,
    { updates },
  );
  return response.data;
};

export const approveChannelFanout = async (outboxEventId: string): Promise<{
  outbox_event_id: string;
  approved_queue_ids: number[];
  approved_by: string;
  copies_promoted?: number;
}> => {
  await ensureApiAuthenticated();
  const response = await api.post(`/admin/channel-approvals/${outboxEventId}/approve`);
  return response.data;
};

export const rejectChannelFanout = async (outboxEventId: string): Promise<{
  outbox_event_id: string;
  rejected_queue_ids: number[];
  rejected_by: string;
  copies_deleted?: number;
}> => {
  await ensureApiAuthenticated();
  const response = await api.post(`/admin/channel-approvals/${outboxEventId}/reject`);
  return response.data;
};

export const removeChannelApprovalVariants = async (
  outboxEventId: string,
  variantIds: string[],
): Promise<ChannelApprovalDetail & {
  removed_count?: number;
  remaining_count?: number;
  rejected?: boolean;
  rejected_queue_ids?: number[];
  copies_deleted?: number;
  removed_by?: string;
}> => {
  await ensureApiAuthenticated();
  const response = await api.post(
    `/admin/channel-approvals/${outboxEventId}/remove-variants`,
    { variant_ids: variantIds },
  );
  return response.data;
};
